import axios from "axios";
import { createHelpers } from "vuex-map-fields";

import {
  dataTableActionsFactory,
  dataTableMutations,
  dataTableState
} from "@/store/abstracts/dataTableStores";

import { RESET_STATE } from "../../mutation-types";

// The API util is used to send the
// data the user enters to our server.
import { RepositoryFactory, USERS_REPOSITORY } from "@/utils/RepositoryFactory";
const UsersRepository = RepositoryFactory.get(USERS_REPOSITORY);

import {
  TOGGLE_LOADING,
  ERROR,
  SUCCESS,
  UPDATE_LIST_ITEM
} from "@/store/mutation-types";

import {
  IS_LOGIN_AVAILABLE,
  DEACTIVATE_ONE,
  DEACTIVATE_MANY
} from "@/store/action-types";

// Models are used to prepare
// store data to be sent to an API.
import { User } from "@/models/User";

// We're using reusable form modules
// to store the data of our forms.
import createForm from "./forms/create";
import searchForm from "./forms/search";

const getDefaultState = () => ({
  ...dataTableState
});

const state = getDefaultState();

let call;

const actions = {
  ...dataTableActionsFactory({
    repository: UsersRepository,
    model: User,
    drawerName: "user"
  }),
  async [IS_LOGIN_AVAILABLE]({ commit }, { login, currentUserId }) {
    if (call) {
      call.cancel("Only one request allowed at a time.");
    }

    try {
      commit(TOGGLE_LOADING, true);

      call = axios.CancelToken.source();

      const { data } = await UsersRepository.isLoginAvailable({
        login,
        currentUserId,
        cancelToken: call.token
      });

      commit(TOGGLE_LOADING, false);

      return data.content.emailAvailable;
    } catch (error) {
      if (!error.__CANCEL__) {
        commit(
          `app/${ERROR}`,
          { error, message: error.message },
          { root: true }
        );
      }
    }

    commit(TOGGLE_LOADING, false);
  },
  async [DEACTIVATE_ONE]({ commit }, entity) {
    try {
      commit(TOGGLE_LOADING, true);

      const tmpEntity = new User(entity);
      tmpEntity.active = false;

      await UsersRepository.put({ entity: tmpEntity, id: tmpEntity.id });

      commit(UPDATE_LIST_ITEM, tmpEntity);

      commit(
        `app/${SUCCESS}`,
        { message: `${entity.entityMessage()} a bien été désactivé` },
        { root: true }
      );
    } catch (error) {
      commit(`app/${ERROR}`, { error, message: error.message }, { root: true });
    }

    commit(TOGGLE_LOADING, false);
  },
  async [DEACTIVATE_MANY]({ commit }, entities = []) {
    try {
      commit(TOGGLE_LOADING, true);

      let tmpEntities = entities.map(entity => new User(entity));

      await UsersRepository.disableUsers({ users: tmpEntities });

      tmpEntities.forEach(entity => {
        entity.active = false;
        commit(UPDATE_LIST_ITEM, entity);
      });

      const message =
        tmpEntities.length > 1
          ? `${tmpEntities.length} ${User.entityName(
              tmpEntities.length > 1
            )} ont bien été désactivés`
          : `${tmpEntities[0].entityMessage()} a bien été désactivé`;

      commit(`app/${SUCCESS}`, { message }, { root: true });
    } catch (error) {
      commit(`app/${ERROR}`, { error, message: error.message }, { root: true });
    }

    commit(TOGGLE_LOADING, false);
  }
};

const mutations = {
  ...dataTableMutations,
  [RESET_STATE](state) {
    Object.assign(state, getDefaultState());
  }
};

const modules = {
  createForm,
  searchForm
};

// We're exporting user field mapper
// functions for mapping form fields to Vuex.
// See: https://github.com/maoberlehner/vuex-map-fields#custom-getters-and-mutations

export const {
  mapFields: mapUserCreateFormFields,
  mapMultiRowFields: mapUserCreateFormMultiRowFields
} = createHelpers({
  getterType: `user/createForm/getField`,
  mutationType: `user/createForm/updateField`
});

export const { mapFields: mapUserSearchFormFields } = createHelpers({
  getterType: `user/searchForm/getField`,
  mutationType: `user/searchForm/updateField`
});

export default {
  namespaced: true,
  actions,
  mutations,
  state,
  modules
};
